import { AxiosError } from 'axios'
import React, { CSSProperties, MouseEventHandler } from 'react'
import { SetRequired } from 'type-fest'

import { TooltipHint } from '@/components/form/FieldLabel'
import { Badge } from '@/components/ui/badge'
import { HydraMember } from '@/types/api'
import {
  CompanyEntity,
  CompanyTypeEntity,
  RoleEntity,
  UserEntity,
} from '@/types/entities'

/**
 * The parts of an address the UI puts on one line. All optional: a payload that does not carry a
 * town, or does not carry an address at all, still formats rather than printing "undefined".
 */
export interface AddressParts {
  addressLine1?: string | null
  city?: string | null
  postcode?: string | null
}

export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

export type ConsentType = 'ICAEW'
export type ConsentDecisionType = 'OPT_IN' | 'OPT_OUT'

export type CompanyTypeCode =
  | IfaCompanyTypeCode
  | AccountantCompanyTypeCode
  | SolicitorCompanyTypeCode
  | MortgageAdviserCompanyTypeCode
  | BusinessLendingNonFCACompanyTypeCode
  | InsuranceAdviserCompanyTypeCode
  | CorporateFinanceCompanyTypeCode
  | CorporateFinanceNonFCACompanyTypeCode
  | InvestmentManagementTypeCode
  | 'auto_enrolment'
  | 'business_coach'
  | 'conveyancing'
  | 'employee_benefits_provider'
  | 'estates_admin'
  | 'estate_planner'
  | 'estates_agent'
  | 'management_consultant'
  | 'marketing_consultant'
  | 'property_developer'
  | 'software_provider'
  | 'surveyor'
  | 'venture_capital'
  | 'other'

export type IfaCompanyTypeCode = 'ifa'
export type MortgageAdviserCompanyTypeCode = 'mortgage_adviser'
export type BusinessLendingNonFCACompanyTypeCode = 'business_lending_non_fca'
export type InsuranceAdviserCompanyTypeCode = 'insurance_adviser'
export type AccountantCompanyTypeCode = 'accountant'
export type SolicitorCompanyTypeCode = 'solicitor'
export type CorporateFinanceCompanyTypeCode = 'corporate_finance'
export type CorporateFinanceNonFCACompanyTypeCode = 'corporate_finance_non_fca'
export type InvestmentManagementTypeCode = 'investment_management'

export type CommercialAgreementType = 'NO' | 'YFS' | 'YJV' | 'RQP'
export type CommercialAgreementDirectionType = 'FROM_TO' | 'TO_FROM'
export type CommercialAgreementReferralDirection = 'OUTGOING' | 'INCOMING'

export type CompassReportStatus = 'INVITED' | 'IN_PROGRESS' | 'COMPLETE'
export type CompassReportPriorityType = 'HIGH' | 'MEDIUM' | 'LOW'
export type CompassPdfReportSection = '1' | '2' | '3' | '4'

export type CompanyEmailType = 'COMPASS_INVITE'
export type CompanyProfileIdentifierType = 'companyId' | 'frn' | 'sraNumber'
export type CompanyDetailsIdentifierType = 'companyId' | 'referralCode'
export type CompanyStatus = 'SUSPENDED' | 'CLAIMED'
export type CompassActionImpact = 'high' | 'medium' | 'low'

export type IntegrationService =
  | 'HUBSPOT'
  | 'INTELLIFLO'
  | 'NYLAS'
  | 'NYLAS_EMAIL'
  | 'NYLAS_CALENDAR'
  | 'PLANNR'
  | 'TESTING'
  | 'XPLAN'
  | 'XERO'

export const INTEGRATION_SERVICE = {
  hubspot: 'HUBSPOT',
  intelliflo: 'INTELLIFLO',
  nylas: 'NYLAS',
  nylasEmail: 'NYLAS_EMAIL',
  nylasCalendar: 'NYLAS_CALENDAR',
  plannr: 'PLANNR',
  xplan: 'XPLAN',
  testing: 'TESTING',
  xero: 'XERO',
} satisfies Record<string, IntegrationService>

export const EMAIL_CREATION_METHOD = {
  FRONTEND: 'FRONTEND',
  OUTLOOK: 'OUTLOOK',
} satisfies Record<string, EmailCreationMethodType>

/** How a referral routing rule allocates referrals within an adviser pool. */
export type PoolRoutingMode =
  | 'LEAST_RECENTLY_ASSIGNED'
  | 'SEND_TO_ALL'
  | 'SENDER_SELECTS'

export const POOL_ROUTING_MODE = {
  leastRecentlyAssigned: 'LEAST_RECENTLY_ASSIGNED',
  sendToAll: 'SEND_TO_ALL',
  senderSelects: 'SENDER_SELECTS',
} satisfies Record<string, PoolRoutingMode>

export interface FcaMonitoring {
  flag: boolean | null
  code: string
  message: string
  lastUpdated: string | null
}

export type ObjectMap<T> = Record<keyof T, keyof T>

export type PanelStatus = 'requested' | 'approved' | 'rejected'

/**
 * Whether a company has Referral discovery.
 *
 * `NONE` means it has never asked; `REQUESTED` means it is waiting on RQ;
 * `GRANTED` means the feature is on. Only RQ can grant it, so requesting only ever
 * produces `REQUESTED`.
 */
export type ReferralDiscoveryAccess = 'NONE' | 'REQUESTED' | 'GRANTED'
export type PanelReferralStats = {
  sent: number
  received: number
  income: number
}
export type PanelPerformanceStats = {
  conversionReferralsOut: number | null
  conversionReferralsIn: number | null
  conversionAllReferrals: number | null
  nps: number | null
  npsCount: number | null
  responseTime: number | null
}

export type PanelRequestApprovalStatus = 'pending' | 'approved' | 'rejected'
export type PanelRequestType = 'existing_company' | 'new_company'

export type PaymentDisplayType = 'BLOCK' | 'BANNER'

export type PaymentStatus = 'PENDING' | 'CONFIRMED'

export type PaymentType = 'DIRECT_DEBIT_MANDATE' | 'ONE_OFF_PAYMENT'

export type PendingPanelAction =
  | 'respondToInvite'
  | 'chaseInvite'
  | 'addCommercialAgreement'
  | 'chaseCurrentAgreementReview'
  | 'reviewCurrentAgreement'
  | 'chasePendingAgreementReview'
  | 'reviewPendingAgreement'
  | 'reviewCurrentIncomingAgreementConsentLetter'
  | 'reviewPendingIncomingAgreementConsentLetter'

export type ReviewType = 'written' | 'phone_interview'

export type ReferralStage =
  | 'referred'
  | 'engaging'
  | 'converting'
  | 'closed_won_recurring'
  | 'closed_won_one_off'
  | 'stalled'
  | 'closed_lost'
  | 'not_sure'

export type ReferralStatusCode =
  | 'form_submission'
  | 'awaiting_client_consent'
  | 'awaiting_client_meeting'
  | 'awaiting_client_schedule_meeting'
  | 'complete'
  | 'initial_meeting_held'
  | 'initial_meeting_scheduled'
  | 'live'
  | 'won_completed'
  | 'not_progressing'
  | 'unable_contact_client'
  | 'awaiting_client_response'
  | 'client_details_passed_to_pco'
  | 'client_postponed_decision'
  | 'reconnect_in_future'
  | 'no_recent_activity'
  | 'still_considering_options'
  | 'not_sure'

export type ReferralApprovalStatus = 'approved' | 'pending' | 'rejected'

export type ReferralType = 'indirect' | 'direct'

export type ReferralTypeOption =
  | 'indirect'
  | 'direct'
  | 'directCc'
  | 'directPco'
  | 'bookCallForClient'
  | 'affiliateLink'

export type ReferralNoteType =
  | 'referral_created'
  | 'referral_updated'
  | 'referral_imported'
  | 'referral_approved'
  | 'referral_rejected'
  | 'referral_forwarded'
  | 'request_client_introduction'
  | 'request_client_fee_consent'
  | 'request_client_bank_details'
  | 'request_client_introduction_consent'
  | 'nothing_to_rebate'
  | 'previously_provided_bank_details'
  | 'initial_meeting_scheduled'
  | 'meeting_booked'
  | 'meeting_rescheduled'

export type NormalReferralTypeOption = Exclude<
  ReferralTypeOption,
  'bookCallForClient'
>

export type ReferralCommissionConsent = 'CONSENT' | 'DO_NOT_CONSENT'

export type ReferralCommissionClientConsentStatus =
  | 'waiting_client_consent'
  | 'waiting_client_bank_details'
  | 'complete'

export type ReferralCommissionRqStatus =
  | 'rq_send_invoice'
  | 'rq_invoice_sent'
  | 'rq_invoice_paid'
  | 'complete'

export type RecommendedPartnerActionType =
  | 'DISMISS'
  | 'ACCEPT_WITH_TERMS'
  | 'ACCEPT_WITHOUT_TERMS'

export interface ReferralCommissionExpectedCommission {
  hasCommercialAgreement?: boolean
  isMateriallyHigher: boolean
  lowerExpectedCommissionRange: number | null
  upperExpectedCommissionRange: number | null
  provisionalRetainAmount: number | null
  provisionalRebateAmount: number | null
}

export interface FcaService {
  category: string
  permissions: string[]
}

export type FeeSharingType = 'PERCENTAGE' | 'SET_AMOUNT'

export type RqCertifiedAssessmentStatus =
  | 'NOT_STARTED'
  | 'PENDING_PAYMENT_CONFIRMATION'
  | 'IN_PROGRESS'
  | 'IN_REVIEW'
  | 'PASSED'
  | 'FAILED'

export type RqCertifiedAssessmentStage = 'one' | 'two'

export enum MutationIconState {
  ok,
  mutating,
  success,
  error,
}

export interface MutationIconProps {
  state: MutationIconState
  message: string
}

export type FormErrors = Record<string, string>

export type TypedFormErrors<Values> = {
  [K in keyof Values]?: string
}

export interface SelectOption<TValue = string> {
  label: string
  value: TValue
  tooltipHint?: TooltipHint
  isDisabled?: boolean
}

export type StateValue = string | number | boolean | number[] | null

export type StateKey =
  | 'interacted_with_rq_certified_announcement'
  | 'interacted_with_walkthrough_banner'
  | 'interacted_with_first_relationship_request_approved_modal'
  | 'interacted_with_intelliflo_integration_autosave_notice'
  | 'interacted_with_icaew_pricing_modal'
  | 'interacted_with_plannr_integration_autosave_notice'
  | 'interacted_with_email_and_calendar_connection_prompt'
  | 'interacted_with_claim_referral_modal'
  | 'client_reviewed_partners'
  | 'panel_approved_new'
  | 'panel_approved_updated'
  | 'panel_requested_new'
  | 'panel_requested_updated'
  | 'panel_request_pending'
  | 'referral_from_new'
  | 'referral_from_updated'
  | 'referral_to_new'
  | 'referral_to_updated'
  | 'referral_approval_request_new'
  | 'relationship_onboarding_modal'
  | 'watched_demo_video'

export type StateType =
  | 'user'
  | 'company'
  | 'companyUser'
  | 'group'
  | 'groupUser'

export const USER_TYPE = {
  client: 'CLIENT',
  company: 'COMPANY',
  icaew: 'ICAEW',
  group: 'GROUP',
  network: 'NETWORK',
} satisfies Record<string, UserType>

export type UserType = 'ICAEW' | 'CLIENT' | 'COMPANY' | 'GROUP' | 'NETWORK'

export type CountryOfIncorporation =
  | 'ENGLAND_WALES'
  | 'SCOTLAND'
  | 'NORTHERN_IRELAND'

export type EmailCreationMethodType = 'FRONTEND' | 'API' | 'OUTLOOK'
export type EmailContentType = 'RQ' | 'PERSONAL' | 'COMPANY'
export type EmailServiceType = 'RQ' | 'SMTP' | 'NYLAS'

export type FcaType =
  | 'appointed_representative'
  | 'directly_authorised'
  | 'trading_name'

export type DoughnutChartSize = 'sm' | 'md'

export type NonNullableAxiosError<T> = SetRequired<AxiosError<T>, 'response'>

export interface SerializableFile {
  name: string
  url: string
  base64: string
}

export type ReferralIndividual = Pick<UserEntity, 'fullName'> & {
  userId: number
  companyUserId: number
  company: HydraMember<
    Pick<CompanyEntity, 'id' | 'presentationName' | 'companyTypeCode'>
  >
}

export type CompanyRegister = 'FCA' | 'CompaniesHouse' | 'SRA'

export interface XplanConfig {
  siteName: string
  hasEverBeenAuthenticated: boolean
  importEnabled: XplanImportEnabledConfig
  mappedCompanyUsersToXplanUsers: Record<number, number | null>
  mappedCompanyReferralStatusesToXplanStatuses: Record<
    number,
    Record<
      number,
      {
        xplanStatus: string | null
        order: number
      }
    >
  >
  defaultReferralStatusByCompanyId: Record<number, number | null>
  fallbackUsers: Record<
    number,
    {
      xplanUserId: number | null
      rqCompanyUserId: number | null
    }
  >
  introducerMapping: Record<number, number | null>
}

export type XplanImportEnabledConfig = Record<number, boolean>

export interface NylasConfig {
  schedulerConfigId: string
  roundRobinAdministratorId?: number
  companyEmail?: {
    email?: string
    grantId?: string
  }
}

export interface NylasEmailConfig {
  grantId: string
  email: string
  provider: NylasProvider
}

export interface NylasCalendar {
  id: string
  name: string
}

export type NylasProvider =
  | 'google'
  | 'microsoft'
  | 'imap'
  | 'icloud'
  | 'yahoo'
  | 'ews'
  | 'zoom'

export interface NylasCalendarConfig {
  email: string
  grantId: string
  provider: NylasProvider
  calendars: NylasCalendar[]
  schedulerConfigId: string
  isOwnerForCompanyConfig: boolean
}

export interface IntellifloConfig {
  adviserMapping: IntellifloAdviserMapping
  statusMapping: {
    [companyId: number]: {
      inbound: IntellifloStatusMapping[]
      outbound: IntellifloStatusMapping[]
    }
  }
  defaultLeadImportStatuses: Record<number, number | null>
  defaultClientImportStatuses: Record<number, number | null>
  defaultServiceCaseImportStatuses?: Record<number, number | null>
  defaultLeadOutboundStatuses: Record<number, string | null>
  defaultClientOutboundStatuses: Record<number, string | null>
  defaultServiceCaseOutboundStatuses?: Record<number, string | null>
  inboundUnmappedBehaviour: Record<number, 'send' | 'skip'>
  outboundUnmappedBehaviour: Record<number, 'send' | 'skip'>
  feeStatusMapping: IntellifloFeeStatusMapping
  documentMapping: IntellifloDocumentMapping
  import: {
    campaign: IntellifloCampaignMapping
    introducers: IntellifloIntroducerMapping
    /** ISO date. When unset the backend falls back to the integration's creation date minus 12 months. */
    startDate?: string | null
    enabled: IntellifloImportEnabledConfig
    groupId?: number | null
    groupName?: string | null
  }
  /** Master import switch. When off, no record type is imported regardless of the gates below. */
  allowImportNewFromIntelliflo?: boolean
  importNewLeads?: boolean
  importNewClients?: boolean
  importNewOpportunities?: boolean
  importNewServiceCases?: boolean
  autoCreateOpportunity?: boolean
  autoCreateServiceCase?: boolean
  serviceCaseCategory?: string
  serviceCaseDescription?: string
  showArchivedCampaignSources: boolean
  /**
   * How RQ decides whether a record already exists in the other system. Optional because configs
   * saved before the section existed have no value; read it through
   * `resolveDuplicateHandling()` so the defaults line up with the backend.
   */
  duplicateHandling?: IntellifloDuplicateHandling
  statusTypePriority?: IntellifloStatusSourceType[]
  opportunityMapping: {
    defaultOpportunityType: number | null
    campaignChannel: number | null
    opportunityTypeMappings?: IntellifloOpportunityTypeMapping[]
    defaultOpportunityStatuses?: Record<number, number | null>
  }
}

/** Whether anything may sync without a person approving it first. */
export type IntellifloDuplicateSyncMode = 'review_all' | 'automatic'

/** What the first matching rule does with the record. */
export type IntellifloDuplicateOutcome = 'match' | 'review' | 'new'

/**
 * The configurable matching rules, in evaluation order. Two rules are deliberately absent because
 * they are not configurable: a record already linked to the other system is always treated as
 * linked, and a search returning more than one candidate always waits for review.
 */
export type IntellifloDuplicateRule =
  | 'exactEmail'
  | 'exactEmailForenameDiffers'
  | 'exactNameAndDateOfBirth'
  | 'exactNameAndMobileOrPostcode'
  | 'exactNameAndLandline'
  | 'similarNameWithIdentifier'
  | 'exactNameOnly'

export interface IntellifloDuplicateHandling {
  syncMode: IntellifloDuplicateSyncMode
  rules: Record<IntellifloDuplicateRule, IntellifloDuplicateOutcome>
}

export type IntellifloImportEnabledConfig = Record<number, boolean>

export interface IntellifloAdviserMapping {
  [companyId: number]: IntellifloCompanyAdviserMapping
}

export interface IntellifloCompanyAdviserMapping {
  [rqUserId: number]: number
}

export interface IntellifloFeeStatusMapping {
  [rqFeeType: string]: string
}

export interface IntellifloDocumentMapping {
  compassReport: {
    category: string
    subcategory: string
  }
  consent: {
    category: string
    subcategory: string
  }
  circumstances: {
    category: string
    subcategory: string
  }
}

export interface IntellifloCampaignMapping {
  [companyIdOrKey: string]: IntellifloCompanyCampaignMapping[]
}

/**
 * Map of a known Intelliflo introducer ID to a user-provided display name.
 * IDs are populated from Intelliflo split templates; the name is set by the user.
 */
export interface IntellifloIntroducerMapping {
  [introducerId: number]: string
}

export interface IntellifloCompanyCampaignMapping {
  profileCompanyId: number
  type: string
  source: string
  introducer?: number | null
}

export interface IntellifloOpportunityTypeMapping {
  opportunityType: number
  serviceAreaId: number | null
}

export type IntellifloStatusType =
  | 'lead'
  | 'client'
  | 'opportunity'
  | 'service_case'

export type IntellifloStatusSourceType =
  | 'lead'
  | 'client'
  | 'opportunity'
  | 'service_case'

export type IntellifloStatusMapping = {
  rq_status: number | null
  io_status: string | null
  io_status_type: IntellifloStatusType
  order: number
}

export interface PlannrConfig {
  allowImportNewFromPlannr?: boolean
  // Per-record-type import gates below the master. Absent means on, matching the backend.
  importNewClients?: boolean
  importNewCases?: boolean
  employeeMapping: PlannrEmployeeMapping
  referralStatusMapping: {
    [companyId: number]: {
      inbound: PlannrStatusMapping[]
      outbound: PlannrStatusMapping[]
    }
  }
  defaultImportStatuses: Record<number, number | null>
  defaultOutboundStatuses: Record<number, string | null>
  // Inbound (Plannr -> RQ) default RQ status for cases, keyed by company id.
  defaultCaseImportStatuses: Record<number, number | null>
  // Outbound (RQ -> Plannr) default case-status uuid, keyed by company id.
  defaultCaseOutboundStatuses: Record<number, string | null>
  // Priority order over ['client', 'case'] deciding which type drives status.
  statusTypePriority: PlannrStatusType[]
  // Whether to auto-create a Plannr case when a client record is created, keyed by company id.
  autoCreateCase: Record<number, boolean>
  // Plannr case-type uuid used when auto-creating a case, keyed by company id.
  caseType: Record<number, string | null>
  // Plannr case-status uuid used when auto-creating a case, keyed by company id.
  caseStatus: Record<number, string | null>
  // Client creation is configured for the integration as a whole, not per company.
  // Whether a client created on Plannr is flagged as a prospect/lead.
  isProspect: boolean
  // What to do when a Plannr client already exists for the client's email address.
  duplicateClientBehaviour: PlannrDuplicateClientBehaviour
  // Whether to email an alert when a duplicate client is detected.
  notifyOnDuplicateClient: boolean
  // Static custom-field values sent on client creation.
  clientCustomFields: PlannrClientCustomField[]
  // Address alerted about duplicates.
  duplicateClientAlertEmail: string | null
  inboundUnmappedBehaviour: Record<number, 'send' | 'skip'>
  outboundUnmappedBehaviour: Record<number, 'send' | 'skip'>
  feeStatusMapping: PlannrFeeStatusMapping
  import: {
    introducer: PlannrIntroducerMapping
    clientImport: {
      per_page: number | null
    } | null
    enabled: PlannrImportEnabledConfig
    // Date-only (YYYY-MM-DD). Null means no gate: clients of any age are imported.
    startDate?: string | null
  }
  defaultedSectionsPendingConfirmation: {
    [key in PlannrConfigSection]?: boolean
  }
  showArchivedCampaignSources: boolean
}

export type PlannrImportEnabledConfig = Record<number, boolean>

export type PlannrConfigSection = keyof Omit<
  PlannrConfig,
  | 'allowImportNewFromPlannr'
  | 'importNewClients'
  | 'importNewCases'
  | 'defaultedSectionsPendingConfirmation'
  | 'showArchivedCampaignSources'
  | 'defaultOutboundStatuses'
  | 'inboundUnmappedBehaviour'
  | 'outboundUnmappedBehaviour'
  | 'defaultCaseImportStatuses'
  | 'defaultCaseOutboundStatuses'
  | 'statusTypePriority'
  | 'autoCreateCase'
  | 'caseType'
  | 'caseStatus'
  | 'isProspect'
  | 'duplicateClientBehaviour'
  | 'notifyOnDuplicateClient'
  | 'clientCustomFields'
  | 'duplicateClientAlertEmail'
>

export interface PlannrEmployeeMapping {
  [companyId: number]: PlannrCompanyEmployeeMapping
}

export interface PlannrCompanyEmployeeMapping {
  [rqUserId: number]: string
}

export interface PlannrFeeStatusMapping {
  [rqFeeType: string]: string
}

export interface PlannrIntroducerMapping {
  [companyId: number]: PlannrCompanyIntroducerUuid[]
}

// NOSONAR: meaningful alias, not redundant
export type PlannrCompanyIntroducerUuid = string

export type PlannrStatusType = 'client' | 'case'

export type PlannrDuplicateClientBehaviour = 'do_not_create' | 'upload_anyway'

// Plannr custom-field types, per POST /api/v1/custom-field.
export type PlannrCustomFieldType =
  | 'string'
  | 'number'
  | 'selection'
  | 'boolean'
  | 'datetime'
  | 'longtext'

// A configured custom field sent to Plannr on client creation. The value is always stored as a
// string and coerced to the field's type by the API before it is sent.
export type PlannrClientCustomField = {
  reference: string
  name: string
  type: PlannrCustomFieldType
  value: string
}

export type PlannrStatusMapping = {
  rq_status: number
  plannr_status: string
  plannr_status_name: string
  plannr_status_type: PlannrStatusType
  order: number
}

export type LockedClientDetailsReason =
  | 'AWAITING_CLIENT_CONSENT'
  | 'AWAITING_CLIENT_CALLBACK'
  | 'INCOMPLETE_COMMERCIAL_AGREEMENT'
  | 'UNEMBEDDABLE_SCHEDULE_CALL_URL'
  | 'AWAITING_CLIENT_DISCLOSURE_CONSENT'

export interface UserCompanyRole {
  companyId: number
  roles: Array<HydraMember<Pick<RoleEntity, 'id' | 'name'>>>
}

export type ReferralLinkScheduler = 'NYLAS' | 'CALENDLY'

export type ReferralDirectionCommercialAgreementStatus =
  | 'CURRENT_AGREEMENT_AWAITING_REVIEW_BY_US'
  | 'CURRENT_AGREEMENT_AWAITING_REVIEW_BY_THEM'
  | 'PENDING_AGREEMENT_AWAITING_REVIEW_BY_US'
  | 'PENDING_AGREEMENT_AWAITING_REVIEW_BY_THEM'
  | 'COMPLETE'

export type CommercialAgreementStatus = 'CURRENT' | 'PENDING'

export type CommercialAgreementReviewStatus =
  | 'AWAITING_REVIEW_BY_US'
  | 'AWAITING_REVIEW_BY_THEM'
  | 'COMPLETE'

export type MeetingFormat =
  | 'video'
  | 'phone'
  | 'in_person_office'
  | 'in_person_client_home'

export type ReferralLinkType = 'GENERIC' | 'COMPASS'

export type ModifierKey = 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'

export type ReferralConsentType =
  | 'YES_E-SIGNATURE'
  | 'NO_CLIENT_LETTER_SIGNED' // Not used anymore
  | 'NO_ADVISOR_WILL_SHARE' // Not used anymore
  | 'NA'

export const REFERRAL_CONSENT_TYPE = {
  yesESignature: 'YES_E-SIGNATURE',
  noClientLetterAlreadySigned: 'NO_CLIENT_LETTER_SIGNED', // Not used anymore
  noAdviserWillShare: 'NO_ADVISOR_WILL_SHARE', // Not used anymore
  na: 'NA',
} satisfies Record<string, ReferralConsentType>

export interface IcaewModals {
  showPricingModal: boolean
  showDataSharingModal: boolean
}

export type ReferralTarget =
  | 'individual'
  | 'serviceArea'
  | 'company'
  | 'companyType'

export type CompassPartnerOrSponsorDetails = Pick<
  CompanyEntity,
  | 'id'
  | 'presentationName'
  | 'scheduleClientCallUrl'
  | 'privacyPolicyUrl'
  | 'logoUrl'
  | 'hasUsableCalendars'
> & {
  requireConsent?: boolean | null
  companyType: HydraMember<Pick<CompanyTypeEntity, 'code'>>
}

export type PanelInviteSource = 'DIRECT_INVITE' | 'REFERRAL_INVITE'

export type CommentEntityAlias =
  | 'PANEL'
  | 'INVITE_REFERRAL_PARTNER'
  | 'REFERRAL_INVITE'

export type ReferralImportDirection = 'TO' | 'FROM'

export type ReferralAccessDeniedReason = 'INSUFFICIENT_PRIVILEGES' | 'OTHER'

export type QueryKeys = ReadonlyArray<Array<unknown>>

export interface SidebarLink {
  href: string
  title: string
  icon: React.ReactElement
  /** Icon shown when the link is active; falls back to `icon` if omitted. */
  activeIcon?: React.ReactElement
  badge?: string
  /**
   * The badge's colour. Left unset for a notification count, which takes its
   * colour from whether the link is active.
   */
  badgeVariant?: React.ComponentProps<typeof Badge>['variant']
  /**
   * Moves the badge into the link's hover tooltip while the sidebar is collapsed
   * to icons, instead of squeezing it onto the icon. For a badge that labels the
   * link ("Beta") rather than one that's news ("3 waiting"), which is worth seeing
   * whatever width the sidebar is at. No effect on mobile, where the sidebar is a
   * drawer that never collapses and no tooltip shows.
   */
  isHoverOnlyWhenCollapsed?: boolean
  onClick?: MouseEventHandler
  onMouseOver?: MouseEventHandler
  style?: React.CSSProperties
  className?: string
  linkClassName?: string
  iconClassName?: string
  comingSoonText?: string
}

export type UserActionName = 'visitedRqViaIntellifloStore'

export type StringMap = Record<string, string>

export interface TableColumn<Field> {
  label: string
  field?: keyof Field
  filter?: () => React.ReactElement
  className?: string
  style?: CSSProperties
  sort?: boolean
  tooltip?: string
}

export type ClientPartyType = 'PERSON' | 'JOINT' | 'CORPORATE' | 'TRUST'

export type PossibleReferralStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED'

// Upper-case, as the API sends and takes them. Anything comparing against the
// lower-case spelling silently never matches.
export type PossibleReferralDirection = 'INBOUND' | 'OUTBOUND'

// Who made the introduction: a firm or one of its staff, one of the current
// company's own clients, or nobody - the client came to us directly. The scan
// never sends DIRECT; only the reviewer picks it. A null from the API is to be
// read as INTRODUCER.
export type PossibleReferralSource = 'INTRODUCER' | 'CLIENT' | 'DIRECT'

/**
 * The OpenAPI schema also lists AWAITING_APPROVAL, which is deliberately absent
 * here: it's an internal state for a scan held at RQ's spend-approval gate, and
 * the API maps it to PENDING on the way out. It only appears in the docs because
 * the schema reflects the whole PHP enum. Don't add it — nothing is ever sent it.
 */
export type EmailReferralScanStatus =
  | 'PENDING'
  | 'FETCHING'
  | 'PROCESSING'
  | 'COMPLETE'
  | 'FAILED'

export type ScannableMailboxProvider =
  | 'microsoft'
  | 'google'
  | 'icloud'
  | 'imap'
  | 'virtual-calendar'
  | 'yahoo'
  | 'ews'

export type ScannableMailboxGrantStatus = 'invalid' | 'connected'

export type MediaObjectContext = 'CLIENT_CONSENT_FILE'

export type ReferralSource = 'INTRODUCER' | 'DIRECT'

export type FileUploadBoxState =
  | 'disabled'
  | 'enabled'
  | 'uploading'
  | 'success'
  | 'error'

export type EmbedCodeTemplateVariant = 'light' | 'dark'

export type RqCustomEvent =
  | 'commercialAgreementTemplateReviewed'
  | 'rateLimited'

export type CommercialAgreementTemplatePlaceholder =
  | 'COMPANY_NAME'
  | 'COMPANY_NUMBER'
  | 'COMPANY_ADDRESS'

export type CannotViewReferralReason =
  | 'DOES_NOT_BELONG_TO_REFERRAL_COMPANIES'
  | 'NON_ADMIN'
  | 'OTHER'

export type ReferralDirection = 'OUTGOING' | 'INCOMING'

export type ReferralArchiveState = 'active' | 'archived' | 'all'

export type DashboardFilters = 'all' | '30d' | '90d' | '180d' | 'ytd' | '365d'

export type DashboardReferralsChartType = 'cumulative' | 'mom'

export type DashboardReferralsChartDataSource = 'referrals' | 'conversions'

// Same as TypeScript's Extract helper but stricter in that it ensures that the
// members you extract are part of the Union type
export type StrictExtract<Union, Members extends Union> = Extract<
  Union,
  Members
>

export type UserContextType = 'company' | 'group'

export type ReferralConsentStage = 'INTRODUCTION' | 'RETROSPECTIVE'

export type ReferralConsentLetterVariant = 'DEFAULT' | 'INTRA_GROUP_TRANSFER'

export type PendingUserAction = 'REVIEW_COMMERCIAL_AGREEMENT_TEMPLATE_REVISIONS'

export type CalendarGrantStatus = 'connected' | 'invalid'
